import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import { DatePickerComponent as CommonDatePickerComponent } from "components/common/date-picker/date-picker";
import { NavController, NavParams, ViewController } from "ionic-angular";
import {
  Order,
  OrderComponent,
  OrderResponse,
  OrderVisitFrequency,
} from "models/order-management/models";
import { StatefulOrderResponse } from "models/order-management/statefullOrderResponse";
import * as moment from "moment-timezone";
import { OrderFormState } from "pages/orders-entry-om/add-order/order-form-state";
import { UtilityService } from "providers/utility-service/utility-service";
import { StateManagementService } from "../../../providers/state-management/state-management.service";
import {
  OrderComponentInterface,
  OrderComponentPropertyUpdate,
  OrderCreateRequestUpdate,
} from "../order-components-interface";

@Component({
  selector: "date-picker",
  templateUrl: "date-picker.html",
})
export class DatePickerComponent implements OrderComponentInterface, OnInit {
  /****************************************************************************
   * ViewChild                                                                *
   ****************************************************************************/
  @ViewChild("datePicker") datePicker: CommonDatePickerComponent;

  /****************************************************************************
   * Inputs                                                                   *
   ****************************************************************************/
  @Input() orderComponent: OrderComponent;
  @Input() orderCreateRequest: Order;
  @Input() set orderFormState(orderFormState: OrderFormState) {
    const componentField = this.orderComponent.properties[0].name;
    const shouldClear = OrderFormState.shouldClear(
      componentField,
      this._orderFormState,
      orderFormState
    );
    this._orderFormState = orderFormState;
    if (shouldClear) {
      if (orderFormState.declined && !this.selectedDate) {
        const updateObject: OrderCreateRequestUpdate = { updates: {} };
        const date = new Date();
        const dateAndTime = this.getDateAndTime(date);
        this.setDefaultValue(dateAndTime);
        updateObject.updates[this.componentField] = dateAndTime.toISOString();
        this.orderCreateRequestEvent.emit(updateObject);
      } else if (!orderFormState.declined) {
        this.selectedDate = "";
        this._lastBenefitPeriodDefault = "";
        this.orderCreateRequestEvent.emit({
          deletes: [componentField as keyof Order],
        });
      }
    }
    if (!this.selectedDate) this.setDefaultValue();
    this.setDiscontinueDate();
    this.checkSpecialValidators();
    this.checkIsValid();
  }

  /****************************************************************************
   * Outputs                                                                  *
   ****************************************************************************/
  @Output() orderCreateRequestEvent =
    new EventEmitter<OrderCreateRequestUpdate>();
  @Output() orderFormStateEvent = new EventEmitter<OrderFormState>();
  @Output() isValidEvent = new EventEmitter<boolean>();
  @Output() amendedOrderRequestEvent =
    new EventEmitter<OrderComponentPropertyUpdate>();

  /****************************************************************************
   * State                                                                    *
   ****************************************************************************/
  defaultStartTime: string = "";
  selectedDate: string = "";
  componentField: string = "";
  errorText: string | undefined;
  warningText: string | undefined;
  complianceWarningText: string | undefined;
  _orderFormState: OrderFormState;
  overlappingVfOrders: StatefulOrderResponse[] = [];
  // Tracks the last auto-filled value from the benefit period so we can distinguish
  // it from a manually-entered date. If selectedDate matches this value, the user
  // hasn't changed it and we can safely update it when the benefit period changes.
  private _lastBenefitPeriodDefault: string = "";

  constructor(
    public navCtrl: NavController,
    public navParams: NavParams,
    public viewCtrl: ViewController,
    public stateData: StateManagementService,
    public utilityService: UtilityService
  ) {}

  /****************************************************************************
   * Getter Methods                                                           *
   ****************************************************************************/
  public get shouldShow() {
    const componentField: string = this.orderComponent.properties[0].name;
    if (["declinedDate"].includes(componentField)) {
      return this._orderFormState.declined;
    }
    return (
      this.orderComponent.properties[0].required ||
      (["discontinueDate"].includes(componentField) &&
        !this._orderFormState.declined)
    );
  }

  get isDisabled() {
    if (
      !this.orderComponent.properties[0].amendable &&
      this._orderFormState.formType === "amendment"
    ) {
      return true;
    }

    if (this.orderCreateRequest.orderType === "VISIT_FREQUENCY") {
      const { timeInterval, duration, declined } = this._orderFormState;
      // Cannot choose startDate until the duration can be calculated
      if (
        !(timeInterval && duration) &&
        this.componentField === "startDate" &&
        // You can choose a start date for declined and PRN Only orders
        !declined &&
        !OrderVisitFrequency.isPRNOnlyOrder(this._orderFormState)
      ) {
        return true;
      } else if (this.componentField === "discontinueDate") {
        // You can choose a discontinue date for PRN Only orders
        return !OrderVisitFrequency.isPRNOnlyOrder(this._orderFormState);
      }
    }

    return false;
  }

  get componentFieldIsLOCStartDate(): boolean {
    return (
      this.orderCreateRequest.orderType === "LEVEL_OF_CARE" &&
      this.componentField === "startDate"
    );
  }
  /****************************************************************************
   * Setter Methods                                                           *
   ****************************************************************************/
  setAmendedValue() {
    const componentField: string = this.orderComponent.properties[0].name;
    this.selectedDate = this.orderCreateRequest[componentField];
  }

  setDiscontinueDate() {
    if (this.componentField !== "discontinueDate") {
      return;
    }
    if (this._orderFormState.declined) {
      this.orderCreateRequestEvent.emit({
        deletes: [this.componentField as keyof Order],
      });
    } else if (
      this.orderCreateRequest.orderType === "VISIT_FREQUENCY" &&
      OrderVisitFrequency.isPRNOnlyOrder(this._orderFormState)
    ) {
      const bpEndDate = this._orderFormState.benefitPeriodCertificationEndDate;
      if (bpEndDate) {
        const newDefault = moment(bpEndDate).endOf("day").toISOString();
        if (
          !this.selectedDate ||
          this.selectedDate === this._lastBenefitPeriodDefault
        ) {
          this.selectedDate = newDefault;
          this._lastBenefitPeriodDefault = newDefault;
          const updateObject: OrderCreateRequestUpdate = { updates: {} };
          updateObject.updates[this.componentField] = this.selectedDate;
          this.orderCreateRequestEvent.emit(updateObject);
        }
      } else if (
        this._lastBenefitPeriodDefault &&
        this.selectedDate === this._lastBenefitPeriodDefault
      ) {
        this.selectedDate = "";
        this._lastBenefitPeriodDefault = "";
        this.orderCreateRequestEvent.emit({
          deletes: [this.componentField as keyof Order],
        });
      }
    } else {
      const discontinueDate = OrderVisitFrequency.calculateDiscontinueDate(
        this._orderFormState.startDate,
        this._orderFormState.durationInterval,
        this._orderFormState.duration
      );
      if (this.isDisabled && discontinueDate) {
        this.selectedDate = discontinueDate.toISOString();
        const updateObject: OrderCreateRequestUpdate = { updates: {} };
        updateObject.updates[this.componentField] = this.selectedDate;
        this.orderCreateRequestEvent.emit(updateObject);
      }
    }
  }

  /****************************************************************************
   * Lifecycle Methods                                                        *
   ****************************************************************************/
  ngOnInit() {
    if (this._orderFormState.formType === "amendment") {
      this.setAmendedValue();
    } else {
      this.componentField = this.orderComponent.properties[0].name;
      const updateObject: OrderCreateRequestUpdate = { updates: {} };
      if (this.orderComponent.properties[0].required) {
        const dateAndTime = this.getDateAndTime();
        this.setDefaultValue(dateAndTime);

        updateObject.updates[this.componentField] = dateAndTime.toISOString();
        this.orderCreateRequestEvent.emit(updateObject);
      }
    }
    this.checkSpecialValidators();
  }

  /****************************************************************************
   * Component Related Methods                                                *
   ****************************************************************************/
  getDateAndTime(date?: Date): Date {
    const componentField: string = this.orderComponent.properties[0].name;
    const mDate = moment(date);
    if (
      mDate.isSame(moment(), "day") &&
      this.orderCreateRequest.orderType !== "LEVEL_OF_CARE"
    ) {
      return new Date();
    } else if (["startDate"].includes(componentField)) {
      return mDate.startOf("day").toDate();
    }
    return mDate.endOf("day").toDate();
  }

  private setDefaultValue(dateAndTime?: Date): void {
    if (this.componentFieldIsLOCStartDate) {
      this.selectedDate = "";
      this.warningText = "Start Date is Required";
    } else {
      if (dateAndTime) {
        this.emitStartDateChange(dateAndTime);
        this.selectedDate = dateAndTime.toISOString();
      }
      this.warningText = "";
    }
  }
  compareOrderLists(
    a: StatefulOrderResponse[] = [],
    b: StatefulOrderResponse[] = []
  ) {
    const aSet = new Set(a.map((order) => order.orderId).sort());
    const bSet = new Set(b.map((order) => order.orderId).sort());
    return Array.from(aSet).join("|") === Array.from(bSet).join("|");
  }

  onDateValueChange(event: string) {
    this.checkSpecialValidators();

    const updateObject: OrderCreateRequestUpdate = { updates: {} };
    if (event) {
      const date = new Date(event);
      const dateAndTime = this.getDateAndTime(date);
      this.emitStartDateChange(dateAndTime);
      this.selectedDate = dateAndTime.toISOString();
      if (this.componentFieldIsLOCStartDate) {
        this.warningText = "";
      }
      const componentField = this.orderComponent.properties[0].name;
      if (this._orderFormState.formType === "amendment") {
        const tempDate = this.formatDate(dateAndTime, null, "true");
        const prevValue = this.formatDate(
          this.orderCreateRequest[componentField],
          null,
          "true"
        );
        if (prevValue !== tempDate) {
          const amendedObject: OrderComponentPropertyUpdate = { updates: {} };
          amendedObject.updates = {
            propertyName: componentField,
            previousValueDisplay: prevValue,
            previousValue: this.orderCreateRequest[componentField],
            newValue: dateAndTime.toISOString(),
          };
          this.amendedOrderRequestEvent.emit(amendedObject);
        } else {
          const amendedObject: OrderComponentPropertyUpdate = {
            updates: {},
            deletes: [componentField as keyof Order],
          };
          this.amendedOrderRequestEvent.emit(amendedObject);
        }
        this.checkIsValid();
      } else {
        updateObject.updates[componentField] = dateAndTime.toISOString();
        this.orderCreateRequestEvent.emit(updateObject);
      }
    } else {
      this.selectedDate = null;
      updateObject.updates[this.componentField] = null;
      this.emitStartDateChange(null);
      this.orderCreateRequestEvent.emit(updateObject);
    }
    this.checkIsValid();
  }

  checkSpecialValidators() {
    if (this.isSameLOCStartDate()) {
      this.errorText = "Duplicate start date. Please choose another date.";
    } else {
      this.errorText = undefined;
    }
    this.setOverlappingVFOrders();
  }

  isSameLOCStartDate() {
    const { locOrders, order } = this._orderFormState;
    const orderType = this.orderCreateRequest.orderType;
    const orderId = order ? order.orderId : undefined;
    const currentLOCOrder = OrderResponse.getCurrentLOC(locOrders);
    const componentField: string = this.orderComponent.properties[0].name;
    if (
      orderType == "LEVEL_OF_CARE" &&
      componentField === "startDate" &&
      currentLOCOrder &&
      currentLOCOrder.orderId !== orderId
    ) {
      // If the successor LOC order was voided, the discontinueDate is stale — use startDate instead
      const hasActiveSuccessor =
        currentLOCOrder.discontinueDate &&
        locOrders?.some(
          (o) =>
            o.orderType === "LEVEL_OF_CARE" &&
            !o.canceledAction &&
            o.orderId !== currentLOCOrder.orderId &&
            Date.parse(o.startDate) > Date.parse(currentLOCOrder.startDate)
        );
      const currentDate = new Date(
        hasActiveSuccessor
          ? currentLOCOrder.discontinueDate
          : currentLOCOrder.startDate
      );
      const selectedDate = new Date(this.selectedDate);
      return (
        currentDate.toDateString() == selectedDate.toDateString() ||
        selectedDate < currentDate
      );
    }
    return false;
  }

  setOverlappingVFOrders() {
    // Skip if:
    // 1. not VF
    // 2. component not startDate
    // 3. PRN Only order
    const componentField: string = this.orderComponent.properties[0].name;
    const { startDate, durationInterval, duration } = this._orderFormState;
    const orderType = this.orderCreateRequest.orderType;
    const discontinueDate = OrderVisitFrequency.calculateDiscontinueDate(
      startDate,
      durationInterval,
      duration
    );
    if (orderType !== "VISIT_FREQUENCY" || componentField !== "startDate") {
      return;
    } else if (!discontinueDate) {
      // Indicates that overlap cannot be calculated
      this.overlappingVfOrders = [];
    } else if (OrderVisitFrequency.isPRNOnlyOrder(this._orderFormState)) {
      this.overlappingVfOrders = [];
    } else {
      this.overlappingVfOrders = this._orderFormState.vfOrders.filter(
        (order) => {
          const [aStart, aEnd, bStart, bEnd] = [
            startDate.valueOf(),
            discontinueDate.valueOf(),
            Date.parse(order.startDate),
            Date.parse(order.discontinueDate),
          ];
          return (
            order.discipline === this._orderFormState.discipline &&
            !OrderVisitFrequency.isPRNOnlyOrder(order) &&
            aStart <= bEnd &&
            bStart <= aEnd
          );
        }
      );
    }

    // Set warning message that orders will be discontinued
    if (this.overlappingVfOrders.length > 0) {
      this.warningText = `*Please note: A visit frequency order for this discipline already exists within the selected date range. Proceeding will discontinue the existing order(s) to prevent overlap and duplication: [${this.overlappingVfOrders
        .map((order) => `${order.orderId}-${order.nameDescription}`)
        .join(", ")}].`;
    } else {
      this.warningText = undefined;
    }

    if (
      !this.compareOrderLists(
        this.overlappingVfOrders,
        this._orderFormState.overlappingVfOrders
      )
    ) {
      this.orderFormStateEvent.emit({
        overlappingVfOrders: this.overlappingVfOrders,
      });
    }

    this.setComplianceWarning(startDate);
  }

  /**
   * NOVA-1439: when the new VF order's start_date is in the past AND at least
   * one of the orders being auto-discontinued has a COMPLETED visit on/after
   * that start_date, surface a second, non-blocking compliance banner.
   * Submit stays enabled. Reads from the pre-fetched
   * latestCompletedVisitByOrderId map populated by AddOrderPage.
   */
  setComplianceWarning(startDate: Date) {
    if (!startDate || this.overlappingVfOrders.length === 0) {
      this.complianceWarningText = undefined;
      return;
    }
    const startOfNewOrder = moment(startDate).startOf("day");
    if (!startOfNewOrder.isBefore(moment().startOf("day"))) {
      this.complianceWarningText = undefined;
      return;
    }
    const latestMap =
      this._orderFormState.latestCompletedVisitByOrderId || {};
    const hasCompletedInRange = this.overlappingVfOrders.some((order) => {
      const latest = latestMap[order.orderId];
      return !!latest && moment(latest).isSameOrAfter(startOfNewOrder);
    });
    if (hasCompletedInRange) {
      this.complianceWarningText =
        "*Please note: One or more completed visits fall within the backdated date range. " +
        "These visits may now be out of compliance with the newly established frequency. " +
        "Please review visit documentation before proceeding.";
    } else {
      this.complianceWarningText = undefined;
    }
  }

  formatDate(value: Date, timezone: string, dateOnly?: string) {
    if (value === null || value === undefined) {
      return "";
    }
    let coalescedTimezone;
    if (timezone) {
      coalescedTimezone = timezone;
    } else {
      coalescedTimezone = moment.tz.guess();
    }
    if (dateOnly === "true") {
      return moment(new Date(value)).tz(coalescedTimezone).format("MM/DD/YYYY");
    } else {
      return moment(new Date(value))
        .tz(coalescedTimezone)
        .format("MM/DD/YYYY HH:mm (z)");
    }
  }

  checkIsValid() {
    if (this.errorText) {
      this.isValidEvent.emit(false);
    } else {
      const componentField: string = this.orderComponent.properties[0].name;
      if (
        !this.orderComponent.properties[0].required &&
        (!!this.selectedDate || ["discontinueDate"].includes(componentField))
      ) {
        if (
          componentField === "discontinueDate" &&
          this.orderCreateRequest.orderType === "VISIT_FREQUENCY" &&
          OrderVisitFrequency.isPRNOnlyOrder(this._orderFormState) &&
          !this.selectedDate
        ) {
          this.isValidEvent.emit(false);
        } else {
          this.isValidEvent.emit(true);
        }
      } else {
        if (this.orderComponent.properties[0].required && !!this.selectedDate) {
          this.isValidEvent.emit(true);
        } else if (
          this._orderFormState.declined &&
          ["declinedDate"].includes(componentField) &&
          !!this.selectedDate
        ) {
          this.isValidEvent.emit(true);
        } else if (
          !this._orderFormState.declined &&
          ["declinedDate"].includes(componentField)
        ) {
          this.isValidEvent.emit(true);
        } else {
          this.isValidEvent.emit(false);
        }
      }
    }
  }

  emitStartDateChange(date: Date) {
    if (this.componentField === "startDate") {
      this.orderFormStateEvent.emit({
        startDate: date,
      });
    }
  }

  isRequired() {
    const componentField: string = this.orderComponent.properties[0].name;
    if (componentField === "declinedDate") {
      return this._orderFormState.declined;
    }
    if (
      componentField === "discontinueDate" &&
      this.orderCreateRequest.orderType === "VISIT_FREQUENCY" &&
      OrderVisitFrequency.isPRNOnlyOrder(this._orderFormState)
    ) {
      return true;
    }
    return this.orderComponent.properties[0].required;
  }

  /****************************************************************************
   * Cypress-Related ("data-cy") Methods                                      *
   ****************************************************************************/
  toKebabCase(label: string): string {
    if (label) {
      return label
        .replace(/^\s+|\s+$/gm, "")
        .replace(/([a-z])([A-Z])/g, "$1-$2")
        .replace(/[\s_]+/g, "-")
        .toLowerCase();
    }
    return "";
  }

  createDataCyAttribute(component: string, option: string): string {
    return `${this.toKebabCase(component)}-option-${this.toKebabCase(option)}`;
  }
}



// WEBPACK FOOTER //
// ./src/components/order-management/date-picker/date-picker.ts